import { useCallback, useEffect, useMemo, useRef } from "react";

import type { FilterState } from "../types.js";
import {
  type ChartTableDevice,
  type ChartTableSelection,
  chartTablesEqual,
  idsToSelection,
  reconcileChartTables,
  selectionToIds,
} from "./chartTables.js";

/**
 * Resolves the shared `tables` URL selection against the current device list.
 * Dashboard Device filter locks the displayed set without writing that lock back into the URL.
 */
export function useSharedChartTables(
  devices: readonly ChartTableDevice[],
  selection: ChartTableSelection,
  onSelectionChange: (next: ChartTableSelection) => void,
  filters: FilterState,
) {
  const deviceKey = devices.map((d) => d.id).join("\0");
  const deviceIds = useMemo(() => (deviceKey === "" ? [] : deviceKey.split("\0")), [deviceKey]);
  const onChangeRef = useRef(onSelectionChange);
  onChangeRef.current = onSelectionChange;

  useEffect(() => {
    const next = reconcileChartTables(selection, deviceIds);
    if (!chartTablesEqual(next, selection)) onChangeRef.current(next);
  }, [deviceIds, selection]);

  const filterLocksSingle =
    filters.deviceId !== "all" && deviceIds.some((id) => id === filters.deviceId);
  const selectedIds = useMemo(
    () => selectionToIds(selection, deviceIds),
    [deviceIds, selection],
  );
  const onSelectedIdsChange = useCallback(
    (next: Set<string>) => {
      onChangeRef.current(idsToSelection(next, deviceIds));
    },
    [deviceIds],
  );

  const useFleetSeries = selection === "all" || filterLocksSingle;
  const effectiveIds = filterLocksSingle ? new Set([filters.deviceId]) : selectedIds;

  return {
    deviceIds,
    selectedIds,
    effectiveIds,
    filterLocksSingle,
    useFleetSeries,
    onSelectedIdsChange,
    tableCount: useFleetSeries ? deviceIds.length : effectiveIds.size,
  };
}
